/**
 * LoginScreen — the single branded login entry (Story ADMIN-W4 AC 4; SSO HRD
 * added in wave S5; split-screen redesign 2026-08-20).
 *
 * The credential path ("Anmelden mit Brokly ID") starts the OIDC code+PKCE
 * redirect and KEYCLOAK hosts username/password + the TOTP screens (incl.
 * first-login CONFIGURE_TOTP setup — AC 7). We KEEP that path — this is not
 * SSO-only.
 *
 * SSO via Home Realm Discovery (S5): a single "Mit SSO anmelden" button opens an
 * email-entry step. On submit the frontend calls the public resolver
 * POST /auth/sso/resolve; enabled → the SAME authorize redirect parameterised
 * with kc_idp_hint=<idpHint> so Keycloak jumps STRAIGHT to the org's IdP
 * (Microsoft/Google). Not enabled → an opaque, enumeration-safe message.
 *
 * This is the SINGLE login entry. The auth gate (routeTree.gen) sends every
 * unauthenticated visitor here with a `redirect` search param marking where to
 * return, so the root URL and any deep link all render THIS screen — no
 * auto-jump to Keycloak's own hosted page. The `redirect` target is carried
 * into whichever path the user picks so post-login lands correctly.
 *
 * The split-screen look is ported from the Keycloak login theme
 * (infra/keycloak-theme/themes/brokly/login) via ./login-theme.css so this
 * page and the Keycloak-hosted password/TOTP/error pages look identical.
 *
 * [Source: docs/stories/admin-w4-broker-auth.md Task 7]
 * [Source: infra/keycloak-theme/themes/brokly/login/brokly-commons.ftl]
 */

import { useCallback, useState } from 'react';
import { startLogin } from './useBrokerAuth';
import { apiFetch } from '../../lib/api-client';
import { usePageRestore } from '../../lib/use-page-restore';
import './login-theme.css';

/** Response of the public Home-Realm-Discovery resolver POST /auth/sso/resolve. */
interface SsoResolveResponse {
  enabled: boolean;
  provider?: 'microsoft' | 'google';
  /** Keycloak IdP alias to route to via kc_idp_hint (present when enabled). */
  idpHint?: string;
}

/** Opaque, enumeration-safe copy shown when the domain is not whitelisted. */
const SSO_NOT_ENABLED_MESSAGE =
  'Ihre Organisation ist noch nicht für SSO freigeschaltet. Bitte wenden Sie sich an Brokly.';
/** Generic failure copy for the resolver call (network / 5xx). */
const SSO_ERROR_MESSAGE = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';

/**
 * The animated Brokly ray (periwinkle mark), copied verbatim from the Keycloak
 * theme's brokly-commons.ftl. Injected as raw markup so the SMIL <animate>
 * filter survives untouched (JSX camel-casing would mangle it). Static, no user
 * input — safe to set as innerHTML.
 */
const RAY_SVG = `
<svg viewBox="-30 -30 251 206" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Brokly">
  <defs>
    <filter id="brokly-wave" x="-40%" y="-40%" width="180%" height="180%">
      <feTurbulence type="fractalNoise" baseFrequency="0.012 0.028" numOctaves="2" seed="7" result="noise">
        <animate attributeName="baseFrequency" values="0.012 0.028; 0.02 0.016; 0.012 0.028" dur="6s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"></animate>
      </feTurbulence>
      <feDisplacementMap in="SourceGraphic" in2="noise" scale="14" xChannelSelector="R" yChannelSelector="G">
        <animate attributeName="scale" values="9; 20; 9" dur="4.2s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"></animate>
      </feDisplacementMap>
    </filter>
  </defs>
  <g class="bk-ray-drift">
    <g class="bk-ray-flap">
      <g filter="url(#brokly-wave)">
        <path d="M135.085,2.434c1.254,0.979 2.05,2.046 2.329,2.442c0.642,0.921 1.158,1.933 1.529,2.992l5.667,16.146c4.537,12.937 13.304,23.513 25.092,30.292l14.517,8.338c0.979,0.567 3.283,2.054 4.758,4.979c1.262,2.5 1.262,4.733 1.254,5.283c-0.017,0.7 -0.15,4.183 -2.663,6.929c-1.529,1.675 -3.462,2.625 -4.2,2.904c-0.317,0.121 -0.629,0.246 -0.938,0.388l-19.046,8.967c-14.021,6.596 -26.3,17.037 -35.146,29.883l-12.05,17.487c-0.142,0.213 -0.275,0.425 -0.404,0.646c-0.671,1.112 -3.221,4.804 -8.083,5.258c-1.483,0.137 -2.975,-0.046 -4.387,-0.504c-3.467,-1.129 -6.171,-3.967 -7.379,-7.408l-5.525,-15.733c-0.162,-0.467 -0.329,-0.925 -0.504,-1.383c-6.104,-16.1 -19.575,-27.612 -36.654,-32.033c-16.754,-4.338 -33.504,-8.675 -50.254,-13.013c-1.721,-0.375 -2.938,-1.762 -2.996,-3.217c-0.071,-1.875 1.779,-3.75 4.179,-3.567c18.121,-3.971 36.246,-7.942 54.367,-11.912c18.517,-4.183 35.8,-15.483 47.271,-31.471c0.325,-0.45 0.646,-0.908 0.962,-1.367l11.933,-17.329c0.125,-0.229 0.317,-0.558 0.575,-0.933c0.438,-0.637 3.975,-5.667 9.575,-5.492c3.25,0.108 5.562,1.912 6.221,2.429" fill="#9FA5FF" fill-rule="nonzero"></path>
      </g>
    </g>
  </g>
</svg>`;

type View = 'choice' | 'sso';

export function LoginScreen({ redirect }: { redirect?: string }) {
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<View>('choice');
  const [email, setEmail] = useState('');
  const [ssoBusy, setSsoBusy] = useState(false);
  const [notEnabled, setNotEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Back from Keycloak restores this page as it was left, mid-redirect: make the buttons usable again.
  usePageRestore(
    useCallback(() => {
      setBusy(false);
      setSsoBusy(false);
    }, []),
  );

  const begin = (target?: string) => {
    setBusy(true);
    void startLogin(target ?? '/');
  };

  const openSso = () => {
    setNotEnabled(false);
    setError(null);
    setView('sso');
  };

  const backToChoice = () => {
    setNotEnabled(false);
    setError(null);
    setView('choice');
  };

  /**
   * Home Realm Discovery: resolve the email's domain to an IdP, then start the
   * authorize redirect with kc_idp_hint (enabled) or show the opaque message.
   */
  const resolveSso = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSsoBusy(true);
    setNotEnabled(false);
    setError(null);
    try {
      const res = await apiFetch<SsoResolveResponse>('/auth/sso/resolve', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed }),
        auth: false, // public, unauthenticated, enumeration-safe resolver
      });
      if (res.enabled && res.idpHint) {
        await startLogin(redirect ?? '/', res.idpHint);
        return; // no state reset — the component unmounts on navigation
      }
      setNotEnabled(true);
      setSsoBusy(false);
    } catch {
      setError(SSO_ERROR_MESSAGE);
      setSsoBusy(false);
    }
  };

  const title = view === 'sso' ? 'Mit SSO anmelden' : 'Willkommen zurück';
  const sub =
    view === 'sso'
      ? 'Geben Sie Ihre geschäftliche E-Mail-Adresse ein. Wir leiten Sie zur Anmeldung Ihrer Organisation weiter.'
      : 'Melden Sie sich an, um zu Ihrem Arbeitsbereich zu gelangen.';

  return (
    <div className="bk-login-root">
      <div className="bk-split">
        {/* ── LEFT: dark hero ─────────────────────────────────────────── */}
        <aside className="bk-hero" aria-hidden="true">
          <div className="bk-glow bk-glow-1" />
          <div className="bk-glow bk-glow-2" />

          <div className="bk-brand">
            <span className="bk-brand-mark">
              <span className="bk-brand-dot" />
            </span>
            <span className="bk-brand-name">Brokly</span>
          </div>

          <div className="bk-hero-center">
            <div className="bk-ray" dangerouslySetInnerHTML={{ __html: RAY_SVG }} />
          </div>

          <div className="bk-tagline">
            <p className="bk-tag-lead">Das Betriebssystem für Schweizer Versicherungsbroker.</p>
            <p className="bk-tag-sub">
              Policen, Renewals, Schäden und Korrespondenz in einem Arbeitsablauf, vorsortiert von
              Brokly&nbsp;KI, entschieden von dir.
            </p>
            <div className="bk-trust">
              <span className="bk-trust-item">
                <span className="bk-trust-dot" />
                Daten in der Schweiz
              </span>
            </div>
          </div>
        </aside>

        {/* ── RIGHT: form panel ───────────────────────────────────────── */}
        <main className="bk-panel">
          <div className="bk-wrap">
            <div className="bk-brand bk-brand-mobile">
              <span className="bk-brand-mark bk-brand-mark-light">
                <span className="bk-brand-dot" />
              </span>
              <span className="bk-brand-name bk-brand-name-dark">Brokly</span>
            </div>

            <div className="bk-head">
              <h1 className="bk-title">{title}</h1>
              <p className="bk-sub">{sub}</p>
            </div>

            {view === 'choice' && (
              <>
                <div className="bk-card">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => begin(redirect)}
                    className="bk-cta"
                  >
                    {busy ? 'Weiterleiten…' : 'Anmelden mit Brokly ID'}
                  </button>
                </div>

                <div className="bk-sso-block">
                  <div className="bk-divider">oder</div>
                  <button type="button" disabled={busy} onClick={openSso} className="bk-secondary">
                    Mit SSO anmelden
                  </button>
                </div>

                <p className="bk-footnote">
                  Beim ersten Login richten Sie die Zwei-Faktor-Authentifizierung ein. Nur mit
                  Einladung Ihres Maklerbüros? Bitte Ihre Administratorin um den Zugang.
                </p>
              </>
            )}

            {view === 'sso' && (
              <>
                {notEnabled && (
                  <div role="alert" className="bk-alert bk-alert-info">
                    {SSO_NOT_ENABLED_MESSAGE}
                  </div>
                )}
                {error && (
                  <div role="alert" className="bk-alert bk-alert-error">
                    {error}
                  </div>
                )}

                <div className="bk-card">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void resolveSso();
                    }}
                  >
                    <div className="bk-field">
                      <label htmlFor="sso-email" className="bk-label">
                        Geschäftliche E-Mail-Adresse
                      </label>
                      <input
                        id="sso-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (notEnabled) setNotEnabled(false);
                          if (error) setError(null);
                        }}
                        className="bk-input"
                        placeholder="name@firma.ch"
                      />
                    </div>

                    <button type="submit" disabled={ssoBusy || !email.trim()} className="bk-cta">
                      {ssoBusy ? 'Weiterleiten…' : 'Weiter'}
                    </button>
                  </form>
                </div>

                <button
                  type="button"
                  onClick={backToChoice}
                  disabled={ssoBusy}
                  className="bk-ghost"
                >
                  Zurück
                </button>
              </>
            )}

            <div className="bk-foot">
              <span className="bk-foot-brand">Brokly</span>
              <span className="bk-foot-sep">·</span>
              <span className="bk-foot-note">Sichere Anmeldung</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
